import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Swords, Trophy, Plus, Copy, Check, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

export function ChallengeHubPage() {
  const [challenges, setChallenges] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showAccept, setShowAccept] = useState(false);
  const [token, setToken] = useState("");
  const [questions, setQuestions] = useState([]);
  const [picked, setPicked] = useState([]);
  const [title, setTitle] = useState("Locked In Challenge");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  async function load() {
    const { data } = await api.get("/locked-in/challenges/mine");
    setChallenges(data || []);
  }
  useEffect(() => {
    load().catch(() => {});
    api.get("/locked-in/questions").then((r) => setQuestions((r.data || []).slice(0, 50))).catch(() => {});
  }, []);

  async function create() {
    if (picked.length < 3) { toast.error("Pick at least 3 questions"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/locked-in/challenges", { title, question_ids: picked });
      toast.success("Challenge created — share the invite link!");
      setShowCreate(false);
      setPicked([]);
      load();
      nav(`/dashboard/locked-in/challenges/${data.challenge_id}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }

  async function accept() {
    setBusy(true);
    try {
      const { data } = await api.post("/locked-in/challenges/accept", { invite_token: token });
      toast.success("Challenge accepted! Time to lock in.");
      setShowAccept(false);
      nav(`/dashboard/locked-in/challenges/${data.challenge_id}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }

  return (
    <div data-testid="challenge-hub">
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-1 flex items-center gap-2">
            <Swords className="w-3 h-3" /> Couple vs Couple
          </p>
          <h1 className="font-serif text-4xl md:text-5xl">The <span className="font-script text-[var(--gold)]">Locked In</span> Challenge</h1>
          <p className="text-sm text-[var(--muted)] mt-2 max-w-xl">
            Face off against another couple. Same questions, private answers, head-to-head reveal at the end.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowAccept(true)} className="btn-outline-gold text-xs" data-testid="open-accept">Accept invite</button>
          <button onClick={() => setShowCreate(true)} className="btn-gold text-xs" data-testid="open-create"><Plus className="w-3 h-3" /> New challenge</button>
        </div>
      </div>

      {challenges.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[var(--line)] rounded-2xl">
          <Swords className="w-10 h-10 mx-auto text-[var(--gold)] mb-3" />
          <p className="font-serif text-2xl">No challenges yet</p>
          <p className="text-sm text-[var(--muted)] mt-2">Create one to invite a couple-friend to face off.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="challenge-list">
          {challenges.map((c) => (
            <Link
              key={c.challenge_id}
              to={`/dashboard/locked-in/challenges/${c.challenge_id}`}
              className="bg-white border border-[var(--line)] rounded-2xl p-5 hover:border-[var(--gold)] transition"
              data-testid={`challenge-card-${c.challenge_id}`}
            >
              <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">{c.status}</p>
              <p className="font-serif text-xl mt-1">{c.title}</p>
              <p className="text-xs text-[var(--muted)] mt-2">
                {c.host_couple_names} vs {c.opponent_couple_names || "Awaiting opponent…"}
              </p>
              {c.status === "completed" && (
                <div className="mt-3 flex items-center gap-3 text-sm">
                  <span className="font-serif text-2xl">{c.host_score}</span>
                  <span className="text-[var(--muted)]">vs</span>
                  <span className="font-serif text-2xl">{c.opponent_score}</span>
                  {c.winner_wedding_id && (
                    <Trophy className={`w-4 h-4 ml-auto ${c.winner_wedding_id === c.host_wedding_id ? "text-[var(--gold)]" : "text-[var(--muted)]"}`} />
                  )}
                </div>
              )}
              <p className="text-[11px] text-[var(--muted)] mt-2">{c.question_ids?.length || 0} questions</p>
            </Link>
          ))}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="create-modal">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-[var(--line)] px-6 py-4 flex items-center justify-between">
              <p className="font-serif text-xl">New Challenge</p>
              <button onClick={() => setShowCreate(false)} className="text-[var(--muted)]"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title (optional)"
                data-testid="challenge-title"
                className="input-luxe"
              />
              <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">Pick 3-20 questions</p>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {questions.map((q) => (
                  <label key={q.question_id} className="flex items-start gap-3 p-3 border border-[var(--line)] rounded-xl cursor-pointer hover:border-[var(--gold)]">
                    <input
                      type="checkbox"
                      checked={picked.includes(q.question_id)}
                      onChange={(e) => setPicked(p => e.target.checked ? [...p, q.question_id] : p.filter(x => x !== q.question_id))}
                      className="mt-1 accent-[var(--gold)]"
                      data-testid={`pick-${q.question_id}`}
                    />
                    <div>
                      <p className="text-sm">{q.text || q.question_text}</p>
                      <p className="text-[10px] text-[var(--muted)] mt-1 uppercase tracking-[0.18em]">{q.category}</p>
                    </div>
                  </label>
                ))}
              </div>
              <button onClick={create} disabled={busy} data-testid="create-submit" className="btn-gold w-full">
                {busy ? "Creating…" : `Create with ${picked.length} questions`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Accept modal */}
      {showAccept && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="accept-modal">
          <div className="bg-white rounded-3xl max-w-md w-full p-6">
            <p className="font-serif text-xl mb-4">Accept Challenge</p>
            <input
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste invite token"
              data-testid="invite-token-input"
              className="input-luxe mb-4"
            />
            <div className="flex gap-2">
              <button onClick={accept} disabled={busy} className="btn-gold flex-1" data-testid="accept-submit">
                {busy ? "Accepting…" : "Accept"}
              </button>
              <button onClick={() => setShowAccept(false)} className="btn-outline-gold">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function ChallengeRoomPage() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [partner, setPartner] = useState("partner_one");
  const [questions, setQuestions] = useState({});

  async function load() {
    const { data } = await api.get(`/locked-in/challenges/${id}`);
    setC(data);
    const map = {};
    (data.my_answers || []).forEach((a) => {
      map[`${a.partner_role}-${a.question_id}`] = a.answer;
    });
    setAnswers(map);
  }
  useEffect(() => { load().catch(() => {}); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch question text for the IDs in this challenge
  useEffect(() => {
    if (!c?.question_ids) return;
    api.get("/locked-in/questions").then((r) => {
      const all = r.data || [];
      const map = {};
      c.question_ids.forEach((qid) => {
        const q = all.find((x) => x.question_id === qid);
        if (q) map[qid] = q;
      });
      setQuestions(map);
    }).catch(() => {});
  }, [c?.question_ids]);

  async function save(qid) {
    const ans = answers[`${partner}-${qid}`];
    if (!ans) return;
    setBusy(true);
    try {
      await api.post(`/locked-in/challenges/${id}/answer`, {
        question_id: qid, answer: ans, partner_role: partner,
      });
      toast.success("Saved");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }

  async function complete() {
    if (!window.confirm("Reveal scores? You won't be able to change answers after.")) return;
    await api.post(`/locked-in/challenges/${id}/complete`);
    load();
  }

  function copyToken() {
    navigator.clipboard.writeText(c.invite_token);
    toast.success("Invite token copied — send it to the other couple");
  }

  if (!c) return null;
  const myWeddingId = c.my_role === "host" ? c.host_wedding_id : c.opponent_wedding_id;

  return (
    <div data-testid="challenge-room">
      <Link to="/dashboard/locked-in/challenges" className="text-xs text-[var(--muted)] hover:text-[var(--gold)] mb-4 inline-block">← All challenges</Link>
      <div className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-1">{c.status}</p>
        <h1 className="font-serif text-4xl md:text-5xl">{c.title}</h1>
        <p className="text-sm text-[var(--muted)] mt-2">{c.host_couple_names} vs {c.opponent_couple_names || "—"}</p>
      </div>

      {c.status === "sent" && c.my_role === "host" && (
        <div className="bg-[#C8A96B]/10 border border-[#C8A96B]/30 rounded-2xl p-5 mb-6" data-testid="invite-block">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-2">Invite a couple to face off</p>
          <p className="text-sm text-[var(--muted)] mb-3">Send this token to your opponent. They paste it in the "Accept invite" box on their dashboard.</p>
          <div className="flex items-center gap-2 bg-white border border-[var(--line)] rounded-xl p-3">
            <code className="text-sm font-mono flex-1 truncate">{c.invite_token}</code>
            <button onClick={copyToken} className="btn-gold text-xs" data-testid="copy-token"><Copy className="w-3 h-3" /> Copy</button>
          </div>
        </div>
      )}

      {c.status === "completed" ? (
        <CompletedView c={c} questions={questions} myWeddingId={myWeddingId} />
      ) : (
        <>
          {/* Partner toggle */}
          <div className="flex gap-2 mb-5">
            {["partner_one", "partner_two"].map((p) => (
              <button
                key={p}
                onClick={() => setPartner(p)}
                data-testid={`partner-${p}`}
                className={`text-xs px-4 py-2 rounded-full border ${partner === p ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white border-[var(--line)] text-[var(--muted)] hover:border-[var(--gold)]"}`}
              >
                {p === "partner_one" ? "Partner 1" : "Partner 2"}
              </button>
            ))}
          </div>

          {/* Questions */}
          <div className="space-y-4">
            {c.question_ids?.map((qid) => {
              const q = questions[qid];
              const value = answers[`${partner}-${qid}`] || "";
              const saved = (c.my_answers || []).some((a) => a.partner_role === partner && a.question_id === qid);
              return (
                <div key={qid} className="bg-white border border-[var(--line)] rounded-2xl p-5" data-testid={`q-${qid}`}>
                  <p className="text-sm font-medium mb-1">{q?.text || q?.question_text || qid}</p>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] mb-3">{q?.category || "—"}</p>
                  <textarea
                    value={value}
                    onChange={(e) => setAnswers({ ...answers, [`${partner}-${qid}`]: e.target.value })}
                    rows={2}
                    placeholder="Your answer…"
                    data-testid={`answer-${qid}`}
                    className="input-luxe resize-none mb-2"
                  />
                  <button onClick={() => save(qid)} disabled={busy || !value} data-testid={`save-${qid}`} className="btn-outline-gold text-xs">
                    {saved ? <><Check className="w-3 h-3" /> Saved · Update</> : "Save"}
                  </button>
                </div>
              );
            })}
          </div>

          {c.status === "in_progress" && (
            <button onClick={complete} className="btn-gold mt-8 w-full md:w-auto" data-testid="complete-btn">
              Reveal scores <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </>
      )}
    </div>
  );
}

function CompletedView({ c, questions, myWeddingId }) {
  const won = c.winner_wedding_id === myWeddingId;
  const tied = !c.winner_wedding_id;
  return (
    <div data-testid="challenge-completed">
      <div className={`rounded-3xl p-8 mb-6 text-center ${won ? "bg-gradient-to-br from-[var(--gold)] to-amber-500 text-white" : "bg-[var(--bg-warm)]"}`}>
        <Trophy className={`w-12 h-12 mx-auto mb-3 ${won ? "text-white" : "text-[var(--gold)]"}`} />
        <p className="font-serif text-3xl">
          {tied ? "It's a tie!" : won ? "You won!" : "They won this round"}
        </p>
        <div className="flex justify-center items-end gap-6 mt-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] opacity-70">{c.host_couple_names}</p>
            <p className="font-serif text-5xl">{c.host_score}</p>
          </div>
          <p className="text-2xl opacity-50 mb-2">vs</p>
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] opacity-70">{c.opponent_couple_names}</p>
            <p className="font-serif text-5xl">{c.opponent_score}</p>
          </div>
        </div>
      </div>

      {/* Question-by-question reveal */}
      <div className="space-y-4">
        {c.question_ids?.map((qid) => {
          const q = questions[qid];
          const myA = (c.my_answers || []).filter((a) => a.question_id === qid);
          const oppA = (c.opponent_answers || []).filter((a) => a.question_id === qid);
          return (
            <div key={qid} className="bg-white border border-[var(--line)] rounded-2xl p-5">
              <p className="font-medium text-sm mb-3">{q?.text || q?.question_text || qid}</p>
              <div className="grid sm:grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--gold)] mb-1">Your couple</p>
                  {myA.map((a) => <p key={a.answer_id} className="text-[var(--muted)]"><strong>{a.partner_role.replace("_", " ")}:</strong> {a.answer}</p>)}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--gold)] mb-1">Opponent</p>
                  {oppA.map((a) => <p key={a.answer_id} className="text-[var(--muted)]"><strong>{a.partner_role.replace("_", " ")}:</strong> {a.answer}</p>)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
